/**
 * Sneakers' mouth, which moves while he talks
 * ([Mascot](../../../docs/MASCOT.md#lip-sync)).
 *
 * The shapes come from the **phonemes the course already carries**, not from a
 * recording ([D-081](../../../docs/DECISIONS.md#d-081)), and the clock comes
 * from `speechSynthesis` — boundary events where a browser sends them, an
 * estimate where it does not.
 *
 * **Drawn over him rather than inside him.** The capybara is a static SVG served
 * as an image; inlining all of it to animate eight pixels of it would put a
 * hundred and forty lines of gradients into every screen he appears on. The
 * mouth is a small overlay positioned from his own coordinates, which means the
 * two cannot drift: both are fractions of the same 240×300 viewBox.
 *
 * **It holds still if a child has asked for less motion.** A mouth flicking
 * through eight shapes a second is precisely the kind of movement
 * `prefers-reduced-motion` exists to stop ([Accessibility](../../../docs/ACCESSIBILITY.md)).
 */

import { type Viseme, visemeAt, type VisemeTrack } from '@capytalk/core';
import { useEffect, useState } from 'react';
import { whileSpeaking } from './audio.js';
import { holdStill } from './prefs.js';

/**
 * Where his own mouth is, so a moving one can cover it.
 *
 * `sneakers.svg` already marks `<g id="mouth">` as *"rig: mouth — swapped per
 * viseme"*, which is exactly this — but it is served as an image and shared
 * byte-for-byte with the mockups, so the group cannot be swapped from here.
 * Covering it is the same result and keeps the two copies identical: **at rest
 * this component draws nothing at all**, and a silent Sneakers is pixel for
 * pixel the one that shipped yesterday.
 *
 * The patch uses **his own gradient**, re-declared in user space over the muzzle's
 * box, rather than a sampled flat colour — a flat one was visibly a lighter
 * rectangle sitting on his face.
 */
const PATCH = 'M92 196 C 92 192 148 192 148 196 L 148 226 C 148 232 92 232 92 226 Z';

/** The lip colour, taken from his own smile so a moving mouth is the same mouth. */
const LIP = '#6B4630';

/**
 * The eight shapes, in his own coordinates.
 *
 * The muzzle runs y 158–238 with the nose band across the top, so the mouth
 * lives between y 196 and 228 — the same box his drawn smile occupies. Open
 * shapes are filled; closed ones are a line, like the original.
 */
const SHAPES: Readonly<Record<Viseme, string>> = {
  // Never drawn: at rest his own smile shows through untouched.
  rest: '',
  AI: 'M98 202 C 108 232 132 232 142 202 C 130 212 110 212 98 202 Z',
  E: 'M96 204 C 107 226 133 226 144 204 C 132 211 108 211 96 204 Z',
  O: 'M105 203 C 106 230 134 230 135 203 C 128 211 112 211 105 203 Z',
  U: 'M111 205 C 112 224 128 224 129 205 C 124 211 116 211 111 205 Z',
  MBP: 'M98 209 C 108 216 132 216 142 209',
  FV: 'M98 207 C 108 219 132 219 142 207 C 132 212 108 212 98 207 Z',
  L: 'M98 202 C 108 230 132 230 142 202 C 130 212 110 212 98 202 Z',
};

/** Open shapes get a dark inside; the closed one is a line. */
const OPEN: ReadonlySet<Viseme> = new Set<Viseme>(['AI', 'E', 'O', 'U', 'L', 'FV']);

export default function Mouth({
  track,
  size,
}: {
  /** The line he is saying, as shapes. */
  readonly track: VisemeTrack;
  /** How big he is being drawn, so the overlay matches. */
  readonly size: number;
}) {
  const [at, setAt] = useState<number | null>(null);

  useEffect(() => {
    if (holdStill()) return;
    return whileSpeaking(setAt);
  }, []);

  const viseme: Viseme = at === null ? 'rest' : visemeAt(track, at);

  return (
    <svg
      viewBox="0 0 240 300"
      width={size}
      height={(size / 240) * 300}
      aria-hidden="true"
      // Over him exactly, and never in the way of the tap that replays the line.
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
    >
      {/*
        `snoutg` from `sneakers.svg`, in user space over the muzzle's own box
        (x 62–178, y 158–238) so the patch is the muzzle rather than a colour
        that looks like it.
      */}
      <defs>
        <radialGradient
          id="capy-snout"
          gradientUnits="userSpaceOnUse"
          cx="106"
          cy="179"
          r="80"
        >
          <stop offset="0%" stopColor="#D6A176" />
          <stop offset="60%" stopColor="#C08A5C" />
          <stop offset="100%" stopColor="#A97545" />
        </radialGradient>
      </defs>
      {/* Nothing at all at rest, so his own smile is the one a still page shows. */}
      {viseme !== 'rest' && (
        <>
          <path d={PATCH} fill="url(#capy-snout)" />
          <path
            d={SHAPES[viseme]}
            fill={OPEN.has(viseme) ? LIP : 'none'}
            stroke={LIP}
            strokeWidth={OPEN.has(viseme) ? 2 : 3.8}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* A tongue, which is the whole difference between L and a vowel. */}
          {viseme === 'L' && (
            <path
              d="M110 218 C 114 210 126 210 130 218 C 126 222 114 222 110 218 Z"
              fill="#D2726F"
            />
          )}
        </>
      )}
    </svg>
  );
}
